import {
  getCollectionsForSku,
  getPartitionsForSkuInCollection,
  OrderGuideSearchItemInfo,
  useSearchItemOrderGuideInfo,
} from '@/hooks/use-search-item-order-guide-info';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { useCallback, useState } from 'react';
import { useGetOrderGuideItem } from './use-get-order-guide-item';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';

interface ResolvedOrderGuideSelection {
  partitionUUID: string;
  sku: ItemListOptionMetadata;
  type: 'resolved';
}

interface UnresolvedOrderGuideSelection {
  info: OrderGuideSearchItemInfo;
  skusInOrderGuide: ItemListOptionMetadata[];
  type: 'unresolved';
}

type OrderGuideSelectionResult =
  | ResolvedOrderGuideSelection
  | UnresolvedOrderGuideSelection;

interface OrderGuideActionSheetState {
  info: OrderGuideSearchItemInfo;
  itemMetadata: ItemListOptionMetadata;
  skusInOrderGuide: ItemListOptionMetadata[];
}

export const useSearchOrderGuideActions = ({
  closeSwipeableRef,
}: {
  closeSwipeableRef?: () => void;
}) => {
  const { variantPackPartitionMap, collections } = useOrderGuideItemsContext();
  const getSearchItemOrderGuideInfo = useSearchItemOrderGuideInfo();
  const { editItemNotes } = useUpdateOrderGuideItemContext();
  const getOrderGuideItem = useGetOrderGuideItem();

  const [addNoteState, setAddNoteState] =
    useState<OrderGuideActionSheetState | null>(null);

  // Returns either a ResolvedSelection (each sku -> collection -> partition only has 1 option)
  // or an UnresolvedSelection (each sku -> collection -> partition has multiple options)
  const resolveOrderGuideSelection = useCallback(
    (
      itemMetadata: ItemListOptionMetadata,
    ): OrderGuideSelectionResult | null => {
      const { info, skusInOrderGuide } =
        getSearchItemOrderGuideInfo(itemMetadata);

      if (skusInOrderGuide.length === 0) {
        return null;
      }

      // when each level has only 1 option, resolve directly without showing action sheet
      if (skusInOrderGuide.length === 1) {
        const sku = skusInOrderGuide[0];
        const collectionsForSku = getCollectionsForSku(info, sku, collections);

        if (collectionsForSku.length === 1) {
          const partitionsForSku = getPartitionsForSkuInCollection(
            info,
            sku,
            collectionsForSku[0].uuid,
            collections,
          );

          if (partitionsForSku.length === 1 && partitionsForSku[0].partition) {
            return {
              partitionUUID: partitionsForSku[0].partition.uuid,
              sku,
              type: 'resolved',
            } as ResolvedOrderGuideSelection;
          }
        }
      }

      return {
        info,
        skusInOrderGuide,
        type: 'unresolved',
      } as UnresolvedOrderGuideSelection;
    },
    [collections, getSearchItemOrderGuideInfo],
  );

  const editSearchItemNotes = useCallback(
    (sku: ItemListOptionMetadata, partitionUUID: string) => {
      const skusWithPartition = sku.skus
        .map((s) => ({ ...s, orderGuidePartitionUUID: partitionUUID }))
        .filter((s) => getOrderGuideItem(s) !== null);

      editItemNotes({
        ...sku,
        orderGuidePartitionUUID: partitionUUID,
        skus: skusWithPartition,
      });
    },
    [editItemNotes, getOrderGuideItem],
  );

  const handleAddNotePress = useCallback(
    (itemMetadata: ItemListOptionMetadata) => {
      closeSwipeableRef?.();
      const result = resolveOrderGuideSelection(itemMetadata);
      if (!result) {
        return;
      }

      // ResolvedSelection: know which item to edit immediately
      if (result.type === 'resolved') {
        editSearchItemNotes(result.sku, result.partitionUUID);
        return;
      }

      // UnresolvedSelection: need to show action sheet to select the item to edit
      setAddNoteState({ ...result, itemMetadata });
    },
    [closeSwipeableRef, editSearchItemNotes, resolveOrderGuideSelection],
  );

  return {
    addNoteState,
    collections,
    editSearchItemNotes,
    handleAddNotePress,
    setAddNoteState,
    variantPackPartitionMap,
  };
};
